/**
 * Admin Login Page
 * Staff-only sign-in for the system administrator console.
 * Visually distinct from the tenant LoginPage so the two are never confused.
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Toast } from '../../components/Toast';

export function AdminLoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAdminAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    clearError();

    try {
      await login(email, password);
      navigate('/admin', { replace: true });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setFormError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-slate-50 rounded-lg shadow-lg p-6 sm:p-8">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Staff Administration</h1>
          <p className="text-slate-600">CRM internal console - authorized personnel only</p>
        </div>

        {(formError || error) && <Toast kind="error">{formError || error}</Toast>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-slate-700 mb-1">
              Email Address
            </label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="staff@company.com"
              required
              className="w-full px-4 py-2.5 sm:py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-600 focus:border-transparent disabled:bg-slate-100"
            />
          </div>

          <div>
            <label htmlFor="admin-password" className="block text-sm font-medium text-slate-700 mb-1">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              placeholder="••••••••"
              required
              className="w-full px-4 py-2.5 sm:py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-600 focus:border-transparent disabled:bg-slate-100"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-slate-800 hover:bg-slate-900 disabled:bg-slate-400 text-white font-semibold min-h-[44px] sm:min-h-0 py-2.5 sm:py-2 px-4 rounded-md transition duration-200 mt-6"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="inline-block py-2 sm:py-0 text-sm text-slate-500 hover:text-slate-700 underline">
            Back to regular login
          </Link>
        </div>
      </div>
    </div>
  );
}
